import * as angular from 'angular';
import * as moment from 'moment';

import { GuidHelper } from '../../../../framework';
import { 
    ComponentDefinition,
    Dictionary,
    DigitalSet,
    EditSpotValue,
    EditSpotValueWithOptions,
    SaveSpotValuesDto,
    TagDefinition
} from '../../../../models';
import { ILoggerService, ISpotValueHelperService, ISpotValuesService } from '../../../../services/';
import { SpotManualInputsWidget } from '../../../../widgets/spotManualInputs/spotManualInputs.widget';

class SpotManualInputsPanelController implements angular.IComponentController {
    beginDate: string;
    editedValues: EditSpotValueWithOptions[];
    formController: angular.IFormController;
    isLoading: boolean;
    loadingMessage: string;

    private _spotValuesEdition: SaveSpotValuesDto;
    private _tagsDictionary: Dictionary<TagDefinition>;

    readonly bulkModalId: string;
    readonly date: Date | string; // I guess, any in parent
    readonly digitalSetDictionary: Dictionary<DigitalSet>;
    readonly item: SpotManualInputsWidget;
    readonly onSave: () => void;
    readonly onSaveStart: () => void;
    readonly onSaveComplete: () => void;
    readonly tags: TagDefinition[];
    readonly values: EditSpotValueWithOptions[];

    static $inject = [
        'spotValuesService',
        'spotValueHelperService',
        'translatedLoggerService'
    ];

    constructor(
        private _spotValuesService: ISpotValuesService,
        private _spotValuesHelperService: ISpotValueHelperService,
        private _loggerService: ILoggerService
    ) {
        this.bulkModalId = GuidHelper.getGuid();
    }

    $onInit() {
        this._spotValuesEdition = {
            added: [],
            deleted: [],
            updated: []
        };
    }

    $onChanges(changes: angular.IOnChangesObject) {
        if (changes['values'] && this.values) {
            this.editedValues = this.values.map(v => Object.assign({}, v));
        }
        if (changes['tags'] && this.tags) {
            const tagsDic: Dictionary<TagDefinition> = {};
            this.tags.forEach(t => tagsDic[t.guid] = t);
            this._tagsDictionary = tagsDic;
        }
        if (changes['date']?.currentValue) {
            this.beginDate = moment(this.date).toISOString();
        }
    }

    addRow() {
        let beginMoment = moment();
        if (this.date) {
            beginMoment = moment(this.date);
        }
        const newValue: EditSpotValue = {
            comment: null,
            dataTransactionId: null,
            logbookEntryId: null,
            momentTimestamp: beginMoment,
            originalTimestamp: null,
            tagGuid: this.tags && this.tags.length ? this.tags[0].guid : '',
            timestamp: beginMoment.toISOString()
        };

        const toAdd = this._spotValuesHelperService.mapSpotValuesToEditSpotValuesWithOptions(
            [newValue],
            this.digitalSetDictionary,
            this._tagsDictionary,
            {},
            () => { });
        toAdd.forEach(d => delete (d.originalTimestamp));

        if (!!this.editedValues) {
            const values = this.editedValues.map(d => d);
            values.splice(0, 0, toAdd[0]);
            this.editedValues = values;
        } else {
            this.editedValues = toAdd;
        }

        this._spotValuesEdition.added = this._spotValuesEdition.added.concat(toAdd);
        if (this.formController) {
            this.formController.$setDirty();
        }
    }

    deleteRow(spotValue: EditSpotValueWithOptions) {
        const values = this.editedValues.map(d => d);
        const index = values.indexOf(spotValue);
        const spliced = values.splice(index, 1);
        this.editedValues = values;
        const addedIndex = this._spotValuesEdition.added.indexOf(spotValue);
        if (addedIndex >= 0) {
            this._spotValuesEdition.added.splice(addedIndex, 1);
        } else {
            this._spotValuesEdition.deleted = this._spotValuesEdition.deleted.concat(spliced);
        }
        const updatedIndex = this._spotValuesEdition.updated.indexOf(spotValue);
        if (updatedIndex >= 0) {
            this._spotValuesEdition.updated.splice(updatedIndex, 1);
        }
    }

    cancel() {
        this._spotValuesEdition = {
            added: [],
            updated: [],
            deleted: []
        };
        if (this.values) {
            this.editedValues = this.values.map(v => Object.assign({}, v));
        } else {
            this.editedValues = [];
        }
        if (this.formController) {
            this.formController.$setPristine();
        }
    }

    save() {
        this.isLoading = true;
        this.loadingMessage = 'Saving spot values';
        try {
            if (this.onSaveStart) {
                this.onSaveStart();
            }
            this._spotValuesEdition.added.forEach(d => d.timestamp = d.momentTimestamp.toISOString());
            this._spotValuesEdition.updated
                .forEach(d => d.timestamp = d.momentTimestamp.toISOString());
            this._spotValuesService
                .save(this._spotValuesEdition, this.item.configuration.launchTask, this.item.configuration.taskOptionsOverride)
                .then(data => {
                    this._loggerService.logSuccess('Spot values successfully saved.');
                    this._spotValuesEdition = {
                        added: [],
                        deleted: [],
                        updated: []
                    };
                    this.formController.$setPristine();
                    if (this.onSave) {
                        this.onSave();
                    }
                })
                .catch(() => this._loggerService.logError('Failed to save spot values.'))
                .finally(() => {
                    this.isLoading = false;
                    if (this.onSaveComplete) {
                        this.onSaveComplete();
                    }
                });
        }
        catch (e) {
            this._loggerService.logError('Failed to save spot values.');
            this.isLoading = false;
        }
    }

    onValueChanged(spotValue: EditSpotValueWithOptions) {
        if (this._spotValuesEdition.added.indexOf(spotValue) < 0 && this._spotValuesEdition.updated.indexOf(spotValue) < 0) {
            if (spotValue.originalTimestamp) {
                this._spotValuesEdition.updated.push(spotValue);
            } else {
                delete (spotValue.originalTimestamp);
                this._spotValuesEdition.added.push(spotValue);
            }
        }
    }
}

export const wtSpotManualInputsPanel: ComponentDefinition = {
    componentId: 'wtSpotManualInputsPanel',
    controller: SpotManualInputsPanelController,
    template: require('./spotManualInputsPanel.component.html'),
    controllerAs: 'vm',
    bindings: {
        canEdit: '<?',
        date: '<',
        digitalSetDictionary: '<',
        isEditMode: '<',
        item: '<',
        onSave: '&?',
        onSaveStart: '&?',
        onSaveComplete: '&?',
        tags: '<',
        values: '<'
    }
};